<template>
    <div>
        <redesign-tag
            :color="tag.color"
            :tag-content="tag.tagContent"
            :animated="tag.animated"
            :icon-size="dynamicSizes.iconSize"
            :text-size="dynamicSizes.textSize"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useTimer } from '@/composables/useTimer'
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import { getActiveLayoutColors, getStatusConfiguration, isStatusAlertsDisabled, isVisualAlertDisabled } from '@/composables/globalData/useLayouts'
import { Extension } from '@/types/extension'
import { Settings } from '@/types/dashboard'
import { shouldEmitRecentlyLoggedOutExpired } from '@/composables/useExpiredRecentlyLoggedOutKeys'

/* Props */
const props = defineProps<{
    extension: Extension,
    settings: Settings
    textSize: string
}>()

/* Emits */
const emit = defineEmits<{
    (event: 'recently-logged-out-expired'): void
}>()

const { startTimer, timerDisplayTime, timerState } = useTimer(true)

const extension = toRef(props, 'extension')
const statusColor = ref('')
const timerSettings = toRef(props, 'settings')

/*computed*/
const statusLimits = computed(() => {
    const statusConfiguration = getStatusConfiguration(extension.value.representativeStatus)
    return {
        warn: +statusConfiguration.warning,
        alert: +statusConfiguration.limit,
    }

})
const limitsColors = computed(() => {
    const { loginStatusLimit, loginStatusWarning } = getActiveLayoutColors()
    return {
        loginStatusWarning,
        loginStatusLimit,
    }
})

const tag = computed(() => {
    return {
        color: statusColor.value,
        animated: !!statusColor.value && !isVisualAlertDisabled.value,
        tagContent: [
            {
                iconName: statusColor.value ? 'Timer' : '',
                text: timerDisplayTime.value
            }
        ]
    }
})

const dynamicSizes = computed(() => {
    return {
        textSize: props.textSize,
        iconSize: parseInt(props.textSize)
    }
})

const isAlertDisabled = computed(() => {
    return isStatusAlertsDisabled(extension.value.representativeStatus)
})

/*WATCH*/
watch(() => timerState.value, (val) => {
    let currentStatusColor = ''

    if (!isAlertDisabled.value && statusLimits.value?.warn && val >= statusLimits.value.warn) {
        currentStatusColor = limitsColors.value.loginStatusWarning
    }
    if (!isAlertDisabled.value && statusLimits.value?.alert && val >= statusLimits.value.alert) {
        currentStatusColor = limitsColors.value.loginStatusLimit
    }
    if (statusColor.value !== currentStatusColor) {
        statusColor.value = currentStatusColor
    }

    if (shouldEmitRecentlyLoggedOutExpired(
        timerSettings.value,
        extension.value.representativeStatus,
        val
    )) {
        emit('recently-logged-out-expired')
    }
})
startTimer(extension, timerSettings)

</script>

<style scoped lang="scss">

</style>
